import { News } from "@/models/types";
import getAllNews from "@/news/getAllNews";
import getNews from "@/news/getNews";
import {
  Pagination as GhostPaginationType,
  PostOrPage,
  Tag,
} from "@tryghost/content-api";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import { Footer } from "../../../../components/Footer";
import { Nav } from "../../../../components/common/FrontendNav";

import { NewsCard } from "@/components/News/NewsCard";
import { ResponsiveContainer } from "@/components/common/ResponsiveContainer";
import { getSidebar } from "@/data/news";
import { useTranslation } from "next-i18next";
import Head from "next/head";
import { Headline } from "../../../../components/common/Headline";
import getTags from "../../../../news/getTags";

type PageProps = {
  news: News | null;
  allNews?: {
    news: News[];
    pagination?: GhostPaginationType;
  };
  sidebar: PostOrPage;
  tags: Tag[];
};

export default function Page({ news, allNews, tags, sidebar }: PageProps) {
  const { t } = useTranslation();
  return (
    <>
      {news && (
        <Head>
          <title>{news.title}</title>
          <meta name="og:title" content={news.title} key="og:title" />
          <meta name="twitter:title" content={news.title} key="twitter:title" />
          <meta name="description" content={news.excerpt} key="description" />
          <meta
            name="og:description"
            content={news.excerpt}
            key="og:description"
          />
          <meta
            name="twitter:description"
            content={news.excerpt}
            key="twitter:description"
          />
          <meta name="og:image" content={news.feature_image} key="og:image" />
          <meta
            name="twitter:image"
            content={news.feature_image}
            key="twitter:description"
          />
        </Head>
      )}

      <div>
        <Nav newsTags={tags} article={news ?? undefined} />
        {news && (
          <div className="container_py">
            <div className="lg:block">
              <div className="relative w-full container container-padding-responsive ">
                <Image
                  src={news.feature_image || "/ttbl_favicon.svg"}
                  alt={news.title}
                  fill
                  className="!relative object-cover block lg:overflow-hidden lg:rounded-lg"
                />
              </div>
              <div className="container container-padding-responsive pt-4 lg:mt-4 lg:pt-0 bg-white">
                <ResponsiveContainer widthLeft={70}>
                  <div className="small-text">
                    <div>
                      <Headline className="m-4">{news.title}</Headline>
                      <div className="p-4 lg:p-0">
                        <div dangerouslySetInnerHTML={{ __html: news.html }} />
                        <div className="max-w-[150px] h-[2px] bg-gray-light my-4 md:my-6 rounded-full" />
                        <div className="flex gap-4 text-gray-text small">
                          <div>{news.authors[0].name}</div>
                          <div>|</div>
                          <div>
                            {new Date(news.published_at).toLocaleDateString(
                              "de-De",
                              {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                              }
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="max-lg:hidden lg:bg-bg-gray lg:p-4 lg:rounded-lg ">
                    {typeof sidebar.html === "string" && (
                      <div dangerouslySetInnerHTML={{ __html: sidebar.html }} />
                    )}
                  </div>
                </ResponsiveContainer>
              </div>
            </div>
            {allNews && allNews.news.length > 0 && (
              <>
                <div className="divider" />
                <Headline className="container">{t("More_News")}</Headline>
                <div className="container">
                  {allNews.news.map((e) => (
                    <React.Fragment key={e.id}>
                      <NewsCard key={e.id} news={e} type="big" />
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        {!news && (
          <div className="container_py">
            <div className="container conrainer-padding-responsive">
              Diese News konnte nicht gefunden werden
            </div>
          </div>
        )}
        <div className="flex justify-center p-4">
          <Link href="/news/all/1" className="btn">
            {t("All_News")}
          </Link>
        </div>
        <Footer />
      </div>
    </>
  );
}

export async function getServerSideProps(context: any) {
  const { res } = context;
  res.setHeader(
    "Cache-Control",
    "public, max-age=1800, stale-while-revalidate=2000"
  );
  const news = await getNews(context.params?.newsId);
  const allNews = news
    ? await getAllNews({
        notId: news.id,
        limit: 6,
        locale: context.locale,
      })
    : null;
  const tags = (await getTags(undefined)).tags;

  const sidebar = await getSidebar(context.locale);

  return {
    props: {
      news: news,
      tags: tags.filter((t) => !t.slug.startsWith("hash-")),
      allNews,
      sidebar,
      ...(await serverSideTranslations(context.locale)),
    },
  };
}
